import { Component, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { Observable, startWith, map, timeout, filter, take, tap } from "rxjs";
import { AddressService } from "src/app/core/services/address.service";
import { CountriesService } from "src/app/core/services/country.services";
import { WorkHistoryService } from "src/app/core/services/work-expercience.service";
import { CloseNotifComponent } from "../close-notif/close-notif.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-edit-work-expercience",
  templateUrl: "./edit-work-expercience.component.html",
  styleUrls: ["./edit-work-expercience.component.scss"],
})
export class EditWorkExpercienceComponent implements OnInit {

  showTagalog: boolean = false;

  isCurrentlyInRole: boolean = false;
  language = "Translate to Tagalog";
  buttonColor = "accent";
  textClass = "text-black";

  minDate: any;
  maxDate: any;

  editWorkExperienceFormGroup: FormGroup;

  successMessage: string = "";
  errorMessage: string = "";

  isLicenseBeingUpdated: boolean = false;
  isLicenseDisabled: boolean = false;

  // City Droopdown
  allCitiesMunicipalities: any[] = [];
  filteredCities: Observable<any[]>;

  // countires
  allCountries: any;
  filteredCountries: Observable<any[]>;
  filteredCountriesFormerOFW: Observable<string[]>;

  employmentTypes: any = [
    { id: 1, name: "Permanent" },
    { id: 2, name: "Contractual" },
    { id: 2, name: "Part-time" },
    { id: 2, name: "Probationary" },
  ];

  countrySelected: any;

  isChecked: boolean = false;

  constructor(
    private fb: FormBuilder,
    private addressService: AddressService,
    private countryService: CountriesService,
    private workExperienceService: WorkHistoryService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {
    const axios = require("axios");
    const moment = require("moment");

    axios
      .get("https://worldtimeapi.org/api/timezone/Asia/Manila")
      .then((response) => {
        const currentDatetime = moment(response.data.datetime);
        const currentYear = currentDatetime.year();

        this.minDate = new Date(currentYear - 150, 0, 1); // 20 years ago
        this.maxDate = new Date(currentYear + 0, 11, 31); // 1 year in the future
      })
      .catch((error) => {
        console.error("Error fetching API:", error);
      });
  }

  ngOnInit(): void {
    this.editWorkExperienceFormGroup = this.fb.group({
      companyName: ["", [Validators.required, this.notEmptyString]],
      country: [
        "",
        [Validators.required, this.notEmptyString],
        [this.validateCountry.bind(this)],
      ],
      cityId: [""],
      startOfContractDate: ["", Validators.required],
      endOfContractData: ["", Validators.required],
      occupationCategory: [""],
      occupation: ["", [Validators.required, this.notEmptyString]],
      occupationOthers: [],
      employmentType: ["", Validators.required],
    });

    this.countrySelected = this.data.selectedItem.country;

    this.editWorkExperienceFormGroup.patchValue({
      companyName: this.data.selectedItem.companyName,
      startOfContractDate: this.data.selectedItem.startOfContractDate
        ? new Date(this.data.selectedItem.startOfContractDate).toISOString()
        : null,
      endOfContractData: this.data.selectedItem.endOfContractData
        ? new Date(this.data.selectedItem.endOfContractData).toISOString()
        : null,
      occupation: this.data.selectedItem.occupation,
      employmentType: this.data.selectedItem.employmentType,
      cityId: this.data.selectedItem.cityName,
    });

    this.addressService.getAllMunicipalitiesCities().subscribe((response) => {
      this.allCitiesMunicipalities = response;
    });

    if (this.countrySelected == "Philippines") {
      this.editWorkExperienceFormGroup.controls.cityId.setValidators(
        Validators.required
      );
    }

    // Filter Cities
    this.filteredCities =
      this.editWorkExperienceFormGroup.controls.cityId.valueChanges.pipe(
        startWith(""),
        map((value) => this._filterCities(value))
      );

    this.allCountries = this.countryService.getAllCountries();

    this.filteredCountries =
      this.editWorkExperienceFormGroup.controls.country.valueChanges.pipe(
        startWith(""),
        map((value) => this._filterCountries(value || ""))
      );

    this.getCurrentlyWorking()  
  }

  // Filter Cities Method

  private _filterCities(value: any): any[] {
    if (typeof value !== "string") {
      return this.allCitiesMunicipalities;
    }

    const filterValue = value.toLowerCase();
    return this.allCitiesMunicipalities.filter((city) =>
      city.name.toLowerCase().includes(filterValue)
    );
  }

  // Update Dropdown value

  onSelectCity(cityId: number) {
    const selectedCity = this.allCitiesMunicipalities.find(
      (city) => city.id === cityId
    );
    if (selectedCity) {
      this.editWorkExperienceFormGroup.controls.cityId.setValue(
        selectedCity.name
      );
    }
  }

  private _filterCountries(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.allCountries.filter((country) =>
      country.name.toLowerCase().includes(filterValue)
    );
  }

  setErrorMessage(messageKey: string) {
    this.translate.get(messageKey).subscribe((res: string) => {
      this.errorMessage = res;
    });
  }

  onCountrySelectionChange(selectedCountry: string) {
    const cityIdControl = this.editWorkExperienceFormGroup.controls.cityId;

    if (selectedCountry === "Philippines") {
      // Apply validators when the field is empty
      if (!cityIdControl.value || cityIdControl.value.trim() === "") {
        this.applyValidators();
      }

      // Apply validators if the user starts typing
      this.editWorkExperienceFormGroup.controls.cityId.valueChanges
        .pipe(
          // Filter out empty values
          filter((value: string) => !!value.trim()),
          // Take only the first non-empty value
          take(1),
          // Apply validators
          tap(() => {
            this.applyValidators();
          })
        )
        .subscribe();
    } else {
      // Clear validators
      cityIdControl.clearValidators();
      cityIdControl.updateValueAndValidity();
    }
  }

  applyValidators() {
    const cityIdControl = this.editWorkExperienceFormGroup.controls.cityId;

    cityIdControl.setValidators([
      Validators.required,
      this.notEmptyString,
      this.validateCity.bind(this),
    ]);
    cityIdControl.clearValidators();
    cityIdControl.updateValueAndValidity();
  }

  updateWorkExperience() {
    if (this.editWorkExperienceFormGroup.invalid) {
      const isCountryFound =
        this.editWorkExperienceFormGroup.controls.country.errors &&
        this.editWorkExperienceFormGroup.controls.country.errors.countryNotFound
          ? this.editWorkExperienceFormGroup.controls.country.errors
              .countryNotFound
          : false;
      const isCityFound =
        this.editWorkExperienceFormGroup.controls.cityId.errors &&
        this.editWorkExperienceFormGroup.controls.cityId.errors.cityNotFound
          ? this.editWorkExperienceFormGroup.controls.cityId.errors.cityNotFound
          : false;

      const companyNameValue =
        this.editWorkExperienceFormGroup.controls.companyName.value;
      const countryValue =
        this.editWorkExperienceFormGroup.controls.country.value;
      const cityValue = this.editWorkExperienceFormGroup.controls.cityId.value;
      const startOfContractValue =
        this.editWorkExperienceFormGroup.controls.startOfContractDate.value;
      const endOfContractValue =
        this.editWorkExperienceFormGroup.controls.endOfContractData.value;
      const positionValue =
        this.editWorkExperienceFormGroup.controls.occupation.value;
      const employmentStatusValue =
        this.editWorkExperienceFormGroup.controls.employmentType.value;

      if (isCountryFound) {
        this.setErrorMessage('errorMessagesWorkExperience.invalidCountry');
      }

      if (isCityFound) {
        this.setErrorMessage('errorMessagesWorkExperience.invalidCity');
      }

      if (this.isCurrentlyInRole) {
        if (countryValue == "Philippines") {
          if (
            !companyNameValue ||
            !startOfContractValue ||
            !cityValue ||
            !positionValue ||
            !employmentStatusValue
          ) {
            this.setErrorMessage('errorMessagesWorkExperience.fillRequiredDetails');
          }
        } else {
          if (
            !companyNameValue ||
            !countryValue ||
            !startOfContractValue ||
            !positionValue ||
            !employmentStatusValue
          ) {
            this.setErrorMessage('errorMessagesWorkExperience.fillRequiredDetails');
          }
        }
      }

      if (!this.isCurrentlyInRole) {
        if (countryValue == "Philippines") {
          if (
            !companyNameValue ||
            !startOfContractValue ||
            !endOfContractValue ||
            !cityValue ||
            !positionValue ||
            !employmentStatusValue
          ) {
            this.setErrorMessage('errorMessagesWorkExperience.fillRequiredDetails');
          }
        } else {
          if (
            !companyNameValue ||
            !countryValue ||
            !startOfContractValue ||
            !endOfContractValue ||
            !positionValue ||
            !employmentStatusValue
          ) {
            this.setErrorMessage('errorMessagesWorkExperience.fillRequiredDetails');
          }
        }
      }

      setTimeout(() => {
        this.errorMessage = "";
      }, 3000);
    } else {
      this.isLicenseBeingUpdated = true;
      this.isLicenseDisabled = true;

      const startOfContractDateValue =
        this.editWorkExperienceFormGroup.controls.startOfContractDate.value;
      const endOfContractDataValue =
        this.editWorkExperienceFormGroup.controls.endOfContractData.value;

      const isValidStartDate = !isNaN(Date.parse(startOfContractDateValue));
      const isValidEndDate = !isNaN(Date.parse(endOfContractDataValue));

      let startOfContractDateISO = null;
      let endOfContractDataISO = null;

      if (isValidStartDate) {
        const patchedStartOfContractDate = new Date(startOfContractDateValue);
        startOfContractDateISO = new Date(
          patchedStartOfContractDate.getTime() -
            patchedStartOfContractDate.getTimezoneOffset() * 60000
        ).toISOString();
      }

      if (isValidEndDate) {
        const patchedEndOfContractData = new Date(endOfContractDataValue);
        endOfContractDataISO = new Date(
          patchedEndOfContractData.getTime() -
            patchedEndOfContractData.getTimezoneOffset() * 60000
        ).toISOString();
      }
      const workExpercienceData = {
        applicantId: this.data.selectedItem.applicantId,
        id: this.data.selectedItem.id,
        country: this.editWorkExperienceFormGroup.controls?.country.value || "",
        companyName:
          this.editWorkExperienceFormGroup.controls?.companyName.value || "",
        cityId: this.editWorkExperienceFormGroup.controls?.cityId.value || "",
        startOfContractDate: startOfContractDateISO,
        endOfContractData: endOfContractDataISO,
        occupationCategory: "",
        occupation:
          this.editWorkExperienceFormGroup.controls?.occupation.value || "",
        employmentType:
          this.editWorkExperienceFormGroup.controls?.employmentType.value || "",
        isCurrent: this.isChecked ? this.isChecked : false,
      };

      const cityName = this.editWorkExperienceFormGroup.controls.cityId.value;

      const selectedCity = this.allCitiesMunicipalities.find(
        (city) => city.name === cityName
      );

      if (selectedCity) {
        const cityId = selectedCity.id;

        workExpercienceData.cityId = cityId;
      } else {
        console.error("City not found in the list:", cityName);
      }

      this.workExperienceService
        .updateWorkExpercience(workExpercienceData)
        .subscribe(() => {
          this.isLicenseBeingUpdated = false;
          this.isLicenseDisabled = false;

          this.successMessage = "Work Expercience item updated";

          setTimeout(() => {
            this.dialog.closeAll();
          }, 3000);
        });
    }
  }

  closeNotif() {
    this.dialog.open(CloseNotifComponent);
  }

  notEmptyString(control) {
    const value = control.value;

    if (value === undefined) {
      return null; // Return null if the value is undefined
    }

    if (value === null || value.trim() === "") {
      return { notEmptyString: true };
    }

    return null;
  }

  async validateCountry(control) {
    const enteredCountry = control.value;
    const countryExists = await this.checkCountryExists(enteredCountry);

    return countryExists ? null : { countryNotFound: true };
  }

  async checkCountryExists(enteredCountry: string): Promise<boolean> {
    // Simulate an asynchronous check
    return new Promise<boolean>((resolve) => {
      const countryExists = this.allCountries.some(
        (country) => country.name === enteredCountry
      );
      resolve(countryExists);
    });
  }

  validateCity(control) {
    const enteredCity = control.value;
    const cityExists = this.allCitiesMunicipalities.some(
      (city) => city.name === enteredCity
    );

    return cityExists ? null : { cityNotFound: true };
  }

  toggleFormField() {
    this.isCurrentlyInRole = !this.isCurrentlyInRole;
    if (this.isCurrentlyInRole) {
      this.editWorkExperienceFormGroup.controls.endOfContractData.removeValidators(
        Validators.required
      );
      this.editWorkExperienceFormGroup.controls.endOfContractData.updateValueAndValidity();
      this.editWorkExperienceFormGroup.get("endOfContractData").setValue("");
    } else {
      this.editWorkExperienceFormGroup.get("endOfContractData").setValue("");
      this.editWorkExperienceFormGroup.controls.endOfContractData.setValidators(
        Validators.required
      );
      this.editWorkExperienceFormGroup.controls.endOfContractData.updateValueAndValidity();
    }
  }

  getCurrentlyWorking() {
    this.isLicenseDisabled = true;
    this.isLicenseBeingUpdated = true;

    this.workExperienceService
      .getWorkHistoryItemDetails(this.data.selectedItem.id)
      .subscribe((response) => {
        this.isLicenseDisabled = false;
        this.isLicenseBeingUpdated = false;

        if (response.isCurrent == true) {
          this.isChecked = true;
          this.isCurrentlyInRole = true;
          this.editWorkExperienceFormGroup.controls.endOfContractData.removeValidators(
            Validators.required
          );
          this.editWorkExperienceFormGroup.controls.endOfContractData.updateValueAndValidity();
          this.editWorkExperienceFormGroup
            .get("endOfContractData")
            .setValue("");
        } else {
          this.isChecked = false;
          this.isCurrentlyInRole = false;

          this.editWorkExperienceFormGroup.controls.endOfContractData.setValidators(
            Validators.required
          );
          this.editWorkExperienceFormGroup.controls.endOfContractData.updateValueAndValidity();
        }
      });
  }

  toggleTagalogTranslation() {
    this.showTagalog = !this.showTagalog;
    this.language =
      this.language === "Translate to Tagalog"
        ? "Translate to English"
        : "Translate to Tagalog";

    this.buttonColor =
      this.language === "Translate to Tagalog" ? "accent" : "primary";
    this.textClass =
      this.language == "Translate to Tagalog" ? "text-black" : "text-white";
    
    this.employmentTypes.forEach(type => {

      if(type.id === 0){
        type.name = type.name === 'Permanent' ? 'Permanente' : 'Permanent'
      }

      if(type.id === 1){
        type.name = type.name === 'Contractual' ? 'Kontraktwal' : 'Contractual'
      }

      if(type.id === 2){
        type.name = type.name === 'Part-time' ? 'Panamantala' : 'Part-time'
      }

      if(type.id === 3){
        type.name = type.name === 'Probationary' ? 'Probasyonaryo' : 'Probationary'
      }

    });
      
  }

}
